import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  Collapse,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  GetApp,
  Close,
  Share,
  Add,
  PhoneIphone,
} from '@mui/icons-material';
import { pwaManager } from '../../utils/pwa';

const InstallBanner: React.FC = () => {
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSDialog, setShowIOSDialog] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Check initial state
    setIsInstalled(pwaManager.getIsInstalled());
    setCanInstall(pwaManager.canInstall());

    // Subscribe to install availability changes
    const unsubscribe = pwaManager.onInstallAvailable((available) => {
      setCanInstall(available);
      if (available && !pwaManager.getIsInstalled()) {
        // Show banner after a delay to not be intrusive
        setTimeout(() => setShowBanner(true), 3000);
      }
    });

    return unsubscribe;
  }, []);

  const handleInstall = async () => {
    if (pwaManager.isIOS() && pwaManager.shouldShowIOSInstructions()) {
      setShowIOSDialog(true);
      return;
    }

    if (!canInstall) {
      return;
    }

    setInstalling(true);
    try {
      const accepted = await pwaManager.showInstallPrompt();
      if (accepted) {
        setShowBanner(false);
        setCanInstall(false);
      }
    } catch (error) {
      console.error('Install failed:', error);
    } finally {
      setInstalling(false);
    }
  };

  const handleCloseBanner = () => {
    setShowBanner(false);
    // Don't show again for this session
    sessionStorage.setItem('installBannerDismissed', 'true');
  };

  const handleCloseIOSDialog = () => {
    setShowIOSDialog(false);
  };

  // Don't show if installed or dismissed this session
  if (isInstalled || sessionStorage.getItem('installBannerDismissed')) {
    return null;
  }

  return (
    <>
      {/* Install Banner */}
      <Collapse in={showBanner && (canInstall || pwaManager.shouldShowIOSInstructions())}>
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            zIndex: 1300,
            p: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <GetApp />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Install Check In App
            </Typography>
            <Typography variant="body2">
              Add to your home screen for quick access and offline use
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleInstall}
            disabled={installing}
            startIcon={installing ? undefined : <GetApp />}
          >
            {installing ? 'Installing...' : 'Install'}
          </Button>
          <IconButton
            color="inherit"
            onClick={handleCloseBanner}
            size="small"
          >
            <Close />
          </IconButton>
        </Paper>
      </Collapse>

      {/* iOS Install Instructions Dialog */}
      <Dialog
        open={showIOSDialog}
        onClose={handleCloseIOSDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PhoneIphone color="primary" />
            <Typography variant="h6">Install on iOS</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" paragraph>
            To install this app on your iOS device:
          </Typography>
          
          <List>
            {pwaManager.getIOSInstallInstructions().map((instruction, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {index + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText primary={instruction} />
              </ListItem>
            ))}
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Look for the Share button:</strong>
              <Share sx={{ mx: 1, verticalAlign: 'middle' }} />
              at the bottom of Safari, then tap "Add to Home Screen"
              <Add sx={{ mx: 1, verticalAlign: 'middle' }} />
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseIOSDialog}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstallBanner;